import {
  useSensorFilterState,
  UseSensorFilterStateReturn,
} from '@components/cockpit/panels/sensorInstallation/useSensorFilterState';
import { createContext, Dispatch, FC, ReactNode, SetStateAction, useContext, useMemo, useState } from 'react';

import { EditableSensorOrSubSensorWithShorthand } from '@/types/sensor/sensorInstallation.types';
import { SensorTypeEnum } from '@/types/sensor/sensors';

type PartialSensor = {
  id: number;
  site: string;
  appCode: string;
  name: string;
};

type CreatedSensorInfo = { type: SensorTypeEnum; uniqueKey: string } | null;

type SensorInstallationContextType = {
  editedSensor: EditableSensorOrSubSensorWithShorthand | null;
  setEditedSensor: (value: EditableSensorOrSubSensorWithShorthand | null) => void;
  createdSensor: CreatedSensorInfo;
  setCreatedSensor: (value: CreatedSensorInfo) => void;
  isNewSensor: boolean;
  sensorToDelete: PartialSensor | null;
  setSensorToDelete: Dispatch<SetStateAction<PartialSensor | null>>;
  onCancel: (createdSensor: CreatedSensorInfo) => void;
};

const SensorInstallationContext = createContext<(SensorInstallationContextType & UseSensorFilterStateReturn) | null>(
  null,
);

export const SensorInstallationProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [editedSensor, setEditedSensor] = useState<EditableSensorOrSubSensorWithShorthand | null>(null);
  const [createdSensor, setCreatedSensor] = useState<CreatedSensorInfo>(null);
  const [sensorToDelete, setSensorToDelete] = useState<PartialSensor | null>(null);
  const isNewSensor = editedSensor !== null && editedSensor.code === '';
  const { state, canReset, updateCategories, updateStatus, updateFilterState, setSortBy, resetFilters } =
    useSensorFilterState();

  const onCancel = (createdSensor: CreatedSensorInfo) => {
    setEditedSensor(null);
    setSensorToDelete(null);
    setCreatedSensor(createdSensor ?? null);
  };

  const value = useMemo(
    () => ({
      editedSensor,
      setEditedSensor,
      createdSensor,
      setCreatedSensor,
      isNewSensor,
      onCancel,
      state,
      canReset,
      sensorToDelete,
      setSensorToDelete,
      updateCategories,
      updateStatus,
      updateFilterState,
      setSortBy,
      resetFilters,
    }),
    [
      canReset,
      createdSensor,
      editedSensor,
      isNewSensor,
      resetFilters,
      sensorToDelete,
      setSortBy,
      state,
      updateCategories,
      updateFilterState,
      updateStatus,
    ],
  );

  return <SensorInstallationContext value={value}>{children}</SensorInstallationContext>;
};

export const useSensorInstallationContext = () => {
  const context = useContext(SensorInstallationContext);
  if (!context) {
    throw new Error('useSensorInstallationContext must be used within a SensorInstallationProvider');
  }
  return context;
};
